import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastComponent: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const duration = toast.duration || 3000;
    const timer = setTimeout(() => {
      onRemove(toast.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return 'border-green-500/50 bg-green-900/20 text-green-300';
      case 'error':
        return 'border-red-500/50 bg-red-900/20 text-red-300';
      case 'warning':
        return 'border-yellow-500/50 bg-yellow-900/20 text-yellow-300';
      case 'info':
        return 'border-blue-500/50 bg-blue-900/20 text-blue-300';
      default:
        return 'border-neutral-500/50 bg-neutral-900/20 text-neutral-300';
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return '✓';
      case 'error':
        return '✗';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return '';
    }
  };

  return (
    <div
      className={`border rounded-lg p-4 mb-2 flex items-start gap-3 shadow-lg backdrop-blur-sm ${getToastStyles()}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex-shrink-0 text-lg font-bold">{getIcon()}</div>
      <div className="flex-1 text-sm font-orbitron">{toast.message}</div>
      <button
        onClick={() => onRemove(toast.id)}
        className="flex-shrink-0 text-lg hover:opacity-70 transition-opacity"
        aria-label={t('toast.close') || 'Close'}
      >
        ×
      </button>
    </div>
  );
};

export default ToastComponent;






